import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageHeader from '../../../shared/components/PageHeader';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import { useCompanyWriteAccess } from '../../../shared/auth/useCompanyWriteAccess';
import './MizanImportPage.css';

const baseEnvironment = process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:8010';
const API_BASE_URL = baseEnvironment.replace(/\/$/, '');

const UI_TEXT = {
  'tr-TR': {
    title: "Mizan - Excel'den Aktarim",
    description: 'Sirket ve donemi secip Excel dosyasini yukleyerek Genel Muhasebe Mizaninizi olusturun.',
    companiesLoadError: 'Sirketler yuklenemedi.',
    periodsLoadError: 'Donemler yuklenemedi.',
    selectExcelFile: 'Lutfen bir Excel dosyasi secin.',
    selectCompany: 'Lutfen bir sirket secin.',
    selectPeriod: 'Lutfen bir donem secin.',
    uploadFailed: 'Mizan yuklenemedi.',
    uploadError: 'Mizan yuklenirken hata olustu.',
    uploadedRows: '{{count}} satir iceri alindi.',
    skippedRows: '{{count}} satir atlandi.',
    selectCompanyPeriod: 'Sirket ve donem secilmelidir.',
    clearConfirm: 'Bu sirket/donem icin mevcut verileri silmek istediginize emin misiniz?',
    clearFailed: 'Veri temizlenemedi.',
    clearedRows: '{{count}} kayit silindi. Yeni Excel dosyasini yukleyebilirsiniz.',
    clearError: 'Veri temizlenirken hata olustu.',
    company: 'Sirket secin',
    companyPlaceholder: 'Sirket secin...',
    period: 'Donem secin',
    periodPlaceholder: 'Donem secin...',
    file: 'Excel dosyasi secin',
    upload: 'Excel Yukle',
    uploadLoading: 'Islem devam ediyor...',
    clear: 'Verileri Temizle',
    clearLoading: 'Temizleniyor...',
    rulesTitle: 'Mizan Yukleme Kurallari',
    rule1: 'Excel basliklari: Hesap Kodu, Hesap Adi, Borc Toplami, Alacak Toplami, Para Birimi.',
    rule2: 'Her satir icin kod ve isim zorunludur; sayilar Turkce/Ingilizce formatlariyla okunur.',
    rule3: 'Donem tanimlamasi icin Sistem Yonetimi -> Donemler bolumunu kullanin.',
    rule4: 'Ayni sirket/donem kombinasyonu tekrar yuklendiginde onceki veriler temizlenir.',
    readOnlyAccess: 'Bu sirket icin yazma yetkiniz yok.'
  },
  'en-US': {
    title: "Trial Balance - Import from Excel",
    description: 'Select company and period, then upload an Excel file to create your General Ledger Trial Balance.',
    companiesLoadError: 'Companies could not be loaded.',
    periodsLoadError: 'Periods could not be loaded.',
    selectExcelFile: 'Please select an Excel file.',
    selectCompany: 'Please select a company.',
    selectPeriod: 'Please select a period.',
    uploadFailed: 'Trial balance could not be uploaded.',
    uploadError: 'An error occurred while uploading trial balance.',
    uploadedRows: '{{count}} rows imported.',
    skippedRows: '{{count}} rows skipped.',
    selectCompanyPeriod: 'Company and period must be selected.',
    clearConfirm: 'Are you sure you want to clear existing data for this company/period?',
    clearFailed: 'Data could not be cleared.',
    clearedRows: '{{count}} records deleted. You can upload a new Excel file now.',
    clearError: 'An error occurred while clearing data.',
    company: 'Select company',
    companyPlaceholder: 'Select company...',
    period: 'Select period',
    periodPlaceholder: 'Select period...',
    file: 'Select Excel file',
    upload: 'Upload Excel',
    uploadLoading: 'Processing...',
    clear: 'Clear Data',
    clearLoading: 'Clearing...',
    rulesTitle: 'Trial Balance Upload Rules',
    rule1: 'Excel headers: Account Code, Account Name, Total Debit, Total Credit, Currency.',
    rule2: 'Code and name are required for each row; numbers are parsed in Turkish/English formats.',
    rule3: 'Use System Management -> Periods to manage period definitions.',
    rule4: 'When the same company/period is uploaded again, previous data is cleared first.',
    readOnlyAccess: 'You do not have write access for this company.'
  }
};

const resolveLocaleKey = (value) => (String(value || '').toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR');

const buildHeaders = () => {
  const token = sessionStorage.getItem('authToken');
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const mapStatusLabel = (raw, isEnglish) => {
  if (!isEnglish) return raw;
  if (raw === 'Aktif') return 'Active';
  if (raw === 'Pasif') return 'Passive';
  return raw;
};

function MizanImportPage() {
  const { language } = useLanguage();
  const localeKey = resolveLocaleKey(language);
  const isEnglish = localeKey === 'en-US';
  const text = UI_TEXT[localeKey];
  const t = useCallback((key) => text[key] || UI_TEXT['tr-TR'][key] || key, [text]);

  const [companies, setCompanies] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const fileInputRef = useRef(null);
  const { canWrite } = useCompanyWriteAccess(selectedCompanyId);

  const companyName = useMemo(() => companies.find((company) => String(company.id) === selectedCompanyId)?.name, [companies, selectedCompanyId]);

  const periodLabel = useMemo(() => {
    const period = periods.find((item) => String(item.id) === selectedPeriodId);
    return period ? `${period.code} (${mapStatusLabel(period.status, isEnglish)})` : '';
  }, [periods, selectedPeriodId, isEnglish]);

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/companies`, { headers: buildHeaders() });
      if (!response.ok) throw new Error(t('companiesLoadError'));
      const data = await response.json();
      setCompanies(data);
    } catch (error) {
      setStatus({ type: 'error', text: error?.message || t('companiesLoadError'), warnings: [] });
    }
  }, [t]);

  const fetchPeriods = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/periods`, { headers: buildHeaders() });
      if (!response.ok) throw new Error(t('periodsLoadError'));
      const data = await response.json();
      setPeriods(data);
    } catch (error) {
      setStatus({ type: 'error', text: error?.message || t('periodsLoadError'), warnings: [] });
    }
  }, [t]);

  useEffect(() => {
    fetchCompanies();
    fetchPeriods();
  }, [fetchCompanies, fetchPeriods]);

  useEffect(() => {
    if (!selectedCompanyId && companies.length > 0) setSelectedCompanyId(String(companies[0].id));
  }, [companies, selectedCompanyId]);

  useEffect(() => {
    if (!selectedPeriodId && periods.length > 0) setSelectedPeriodId(String(periods[0].id));
  }, [periods, selectedPeriodId]);

  const handleFileChange = (event) => {
    setFile(event.target.files?.[0] || null);
    setStatus(null);
  };

  const showStatus = (type, message, warnings = []) => {
    if (!message) {
      setStatus(null);
      return;
    }
    setStatus({ type, text: message, warnings });
  };

  const handleUpload = async () => {
    if (!canWrite) {
      showStatus('error', t('readOnlyAccess'));
      return;
    }
    if (!file) {
      showStatus('error', t('selectExcelFile'));
      return;
    }
    if (!selectedCompanyId) {
      showStatus('error', t('selectCompany'));
      return;
    }
    if (!selectedPeriodId) {
      showStatus('error', t('selectPeriod'));
      return;
    }

    setLoading(true);
    showStatus(null, '');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('companyId', selectedCompanyId);
      formData.append('periodId', selectedPeriodId);

      const response = await fetch(`${API_BASE_URL}/api/trial-balance/upload`, {
        method: 'POST',
        headers: buildHeaders(),
        body: formData
      });

      const payload = await response.json();
      if (!response.ok) throw new Error(payload.message || t('uploadFailed'));

      let message = t('uploadedRows').replace('{{count}}', String(payload.inserted));
      if (payload.skipped > 0) message += ` ${t('skippedRows').replace('{{count}}', String(payload.skipped))}`;
      if (payload.warnings?.length > 0) showStatus('success', message, payload.warnings);
      else showStatus('success', message);

      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      showStatus('error', error?.message || t('uploadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleClearData = async () => {
    if (!canWrite) {
      showStatus('error', t('readOnlyAccess'));
      return;
    }
    if (!selectedCompanyId || !selectedPeriodId) {
      showStatus('error', t('selectCompanyPeriod'));
      return;
    }
    if (!window.confirm(t('clearConfirm'))) return;

    setLoading(true);
    showStatus(null, '');

    try {
      const formData = new FormData();
      formData.append('companyId', selectedCompanyId);
      formData.append('periodId', selectedPeriodId);

      const response = await fetch(`${API_BASE_URL}/api/trial-balance/clear`, {
        method: 'POST',
        headers: buildHeaders(),
        body: formData
      });

      const payload = await response.json();
      if (!response.ok) throw new Error(payload.message || t('clearFailed'));

      showStatus('success', t('clearedRows').replace('{{count}}', String(payload.deleted)));
    } catch (error) {
      showStatus('error', error?.message || t('clearError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card mizan-card">
      <PageHeader title={t('title')} description={t('description')} />

      <div className="mizan-form">
        <div className="mizan-grid">
          <label className="mizan-field">
            {t('company')}
            <select value={selectedCompanyId} onChange={(event) => setSelectedCompanyId(event.target.value)} disabled={loading}>
              <option value="">{t('companyPlaceholder')}</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>{company.name}</option>
              ))}
            </select>
          </label>

          <label className="mizan-field">
            {t('period')}
            <select value={selectedPeriodId} onChange={(event) => setSelectedPeriodId(event.target.value)} disabled={loading}>
              <option value="">{t('periodPlaceholder')}</option>
              {periods.map((period) => (
                <option key={period.id} value={period.id}>
                  {period.code} {period.description ? `- ${period.description}` : ''} ({mapStatusLabel(period.status, isEnglish)})
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="mizan-field">
          {t('file')}
          <input type="file" accept=".xlsx,.xls" onChange={handleFileChange} disabled={loading} ref={fileInputRef} />
          {file && <span className="mizan-file-note">{file.name}</span>}
        </label>

        <div className="mizan-actions">
          <button type="button" className="btn-primary" onClick={handleUpload} disabled={!canWrite || loading || !file || !selectedCompanyId || !selectedPeriodId}>
            {loading ? t('uploadLoading') : t('upload')}
          </button>
          <button type="button" className="btn-danger" onClick={handleClearData} disabled={!canWrite || loading || !selectedCompanyId || !selectedPeriodId}>
            {loading ? t('clearLoading') : t('clear')}
          </button>
        </div>
      </div>

      {status && status.text && (
        <div className={`alert ${status.type === 'error' ? 'alert-error' : 'alert-success'}`}>
          <p>{status.text}{companyName ? ` (${companyName}${periodLabel ? ` - ${periodLabel}` : ''})` : ''}</p>
          {status.warnings?.length > 0 && (
            <ul className="mizan-warnings">
              {status.warnings.map((warning) => (<li key={warning}>{warning}</li>))}
            </ul>
          )}
        </div>
      )}

      <div className="mizan-instructions">
        <h3>{t('rulesTitle')}</h3>
        <ul>
          <li>{t('rule1')}</li>
          <li>{t('rule2')}</li>
          <li>{t('rule3')}</li>
          <li>{t('rule4')}</li>
        </ul>
      </div>
    </div>
  );
}

export default MizanImportPage;
